import React, { useRef, useState } from 'react';
import { Download, FileText, Loader2, Trash2, Upload } from 'lucide-react';
import { assessmentApi } from '../../services/assessmentApi';

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  return bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

// Opens a PDF fetched with the user's token. The tab is opened inside the
// click (so popup blockers allow it) and pointed at the file once it arrives;
// if the browser blocks it anyway, the file is downloaded instead.
export async function openBlob(getBlob, filename) {
  const tab = window.open('', '_blank');
  if (tab) {
    tab.document.title = 'Preparing preview…';
    tab.document.body.innerHTML = '<p style="font-family:sans-serif;color:#475569;padding:24px">Preparing preview…</p>';
  }
  try {
    const blob = await getBlob();
    const url = URL.createObjectURL(blob);
    if (tab) {
      tab.location.href = url;
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    if (tab) tab.close();
    throw err;
  }
}

// "Preview … PDF": syncs the latest answers first so the preview matches the
// screen, then opens the PDF. Needs a connection.
export function PreviewButton({ label, flush, fetchPdf, filename, online, className = '' }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await openBlob(async () => {
        await flush();
        return fetchPdf();
      }, filename);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className={`inline-flex flex-col ${className}`}>
      <button type="button" onClick={run} disabled={busy || !online}
        title={online ? undefined : 'Previews need an internet connection'}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
        {label}
      </button>
      {error && <span className="mt-1 text-xs text-red-600">{error}</span>}
    </span>
  );
}

// The memo section's header: type the memo (default) or upload a file. An
// uploaded file takes the place of the typed memo until it is removed.
export function MemoPanel({ uuid, upload, online, disabled, onServer, flush, onDetail, mode, onMode }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const canUpload = online && onServer && !disabled;

  const send = async (file) => {
    if (!file) return;
    setBusy('upload');
    setError(null);
    try {
      await flush().catch(() => {});
      onDetail(await assessmentApi.uploadMemo(uuid, file));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove the uploaded memo? The typed memo will be used instead.')) return;
    setBusy('remove');
    setError(null);
    try {
      onDetail(await assessmentApi.removeMemo(uuid));
      onMode('type');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const tab = (id, label) => (
    <button type="button" role="tab" aria-selected={mode === id} onClick={() => onMode(id)}
      className={`rounded-md px-3 py-1.5 text-sm ${mode === id ? 'bg-white font-medium text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
      {label}
    </button>
  );

  return (
    <div className="mt-3 space-y-3">
      <div role="tablist" className="inline-flex rounded-lg bg-slate-100 p-1">
        {tab('type', 'Type the memo')}
        {tab('upload', 'Upload a memo file')}
      </div>

      {mode === 'type' && upload && (
        <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200">
          An uploaded file ({upload.fileName}) is being used as the memo. Remove it on the Upload tab to use the
          typed memo instead.
        </p>
      )}

      {mode === 'upload' && (
        <div className="rounded-lg border border-dashed border-slate-300 p-4">
          {upload ? (
            <div className="flex flex-wrap items-center gap-3">
              <FileText className="h-8 w-8 shrink-0 text-blue-600" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-900">{upload.fileName}</p>
                <p className="text-xs text-slate-500">
                  {formatSize(upload.fileSize)}
                  {upload.uploadedAt && ` · uploaded ${new Date(upload.uploadedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`}
                  {upload.uploadedBy && ` by ${upload.uploadedBy}`}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" disabled={!online}
                  onClick={() => openBlob(() => assessmentApi.memoFile(uuid), upload.fileName).catch((err) => setError(err.message))}
                  className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                  <Download className="h-3.5 w-3.5" /> Open
                </button>
                <button type="button" disabled={!canUpload || !!busy} onClick={() => inputRef.current?.click()}
                  className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                  <Upload className="h-3.5 w-3.5" /> Replace
                </button>
                <button type="button" disabled={!canUpload || !!busy} onClick={remove}
                  className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2.5 py-1.5 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50">
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <button type="button" disabled={!canUpload || !!busy} onClick={() => inputRef.current?.click()}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
                {busy === 'upload' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {busy === 'upload' ? 'Uploading…' : 'Choose a PDF or Word file'}
              </button>
              <p className="mt-2 text-xs text-slate-500">
                {!online ? 'Uploading needs an internet connection. You can type the memo offline.'
                  : !onServer ? 'Available once this form has synced.'
                    : 'PDF or Word (.docx), up to 16 MB. It replaces the typed memo.'}
              </p>
            </div>
          )}
          <input ref={inputRef} type="file" className="hidden" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => send(e.target.files?.[0])} />
        </div>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
